import { FaLink } from "react-icons/fa";
import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  Text,

  useDisclosure,
  Input,
  ModalFooter,
  Textarea,

  Select,
  Box
} from "@chakra-ui/react";
import axios from "axios";
import { API_URL } from "../config";
import { getToken } from "../token";

import { FORUM, TRANSITION } from "../Theme/styles";
import { columnsFor } from "../columns";
import { categoryForRole, TASK_PATH } from "../categories";
import { priorityName } from "../priorities";
import { formatDate } from "../dates";
import AssignmentSection from "./AssignmentSection";
import AddAssignment from "./AddAssignment";
import { canManage } from "../permissions";
import TaskDescription from "./TaskDescription";
import ImagePicker from "./ImagePicker";

const STATUS_ORDER = [
  "do_realizacji",
  "w_trakcie",
  "do_sprawdzenia",
  "do_poprawy",
  "ukonczone",
];

const STATUS_NAME = {
  do_realizacji: "Do realizacji",
  w_trakcie: "W trakcie",
  ukonczone: "Ukończone",
  do_sprawdzenia: "Do sprawdzenia",
  do_poprawy: "Do poprawy",
};

const KOLOR_STANU = {
  do_realizacji: "#E0A45C",
  w_trakcie: "#6C99FF",
  ukonczone: "#5CC08F",
  do_sprawdzenia: "#B08CD9",
  do_poprawy: "#F08A80",
};

const KOLOR_PRIORITYU = {
  wysoki: "#F08A80",
  sredni: "#E0A45C",
  niski: FORUM.tekstPrzygaszony,
};

const TaskModal = (props) => {
  const [data, setData] = useState({});
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [piority, setPiority] = useState("");
  const [deadline, setDeadline] = useState("");
  const [noweZdjecia, setNoweZdjecia] = useState([]);
  const [changingStatus, setZmieniamStan] = useState(false);
  const [showHistory, setPokazHistorie] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    const url = `${window.location.origin}${TASK_PATH}/${props.taskId}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => window.prompt("Skopiuj link:", url));
  };

  const dataUtworzenia = (() => {
    if (data?.created_at) return data.created_at;
    const wpisy = data?.log || [];
    const powstanie = wpisy.filter((w) => w.event === "task_created").pop();
    if (powstanie) return powstanie.created_at;
    const najstarszy = wpisy[wpisy.length - 1];
    return najstarszy ? najstarszy.created_at : null;
  })();

  const mojeCzesci = (data?.assignments || []).filter(
    (p) => p.team === props.category
  );
  const mojPrzydzial = mojeCzesci.length
    ? mojeCzesci.reduce((furthest, p) =>
        STATUS_ORDER.indexOf(p.status) > STATUS_ORDER.indexOf(furthest.status) ? p : furthest
      )
    : null;

  const changeStatus = (nowy) => {
    if (!data || !props.category || nowy === mojPrzydzial?.status || changingStatus) return;
    setZmieniamStan(true);
    axios
      .put(
        `${API_URL}/tasks/${props.taskId}/status`,
        { status: nowy, team: props.category },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      )
      .then(fetchData)
      .catch((err) => console.error("Nie udało się zmienić stanu", err))
      .finally(() => setZmieniamStan(false));
  };

  const handleTaskUpdate = () => {
    axios
      .put(
        `${API_URL}/tasks/${props.taskId}`,
        {
          title,
          description: [description.trim(), ...noweZdjecia].filter(Boolean).join(String.fromCharCode(10)),
          piority,
          deadline: deadline || null,
          category: props.category,
        },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      )
      .then(() => {
        setNoweZdjecia([]);
        fetchData();
        onEditClose();
      })
      .catch((err) => console.error("Nie udało się zapisać zadania", err));
  };

  const fetchData = useCallback(() => {
    axios
      .get(`${API_URL}/tasks/${props.taskId}`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      })
      .then((res) => {
        setData(res.data);
        setTitle(res.data.title);
        setDescription(res.data.description);
        setPiority(res.data.piority);
        setDeadline(res.data.deadline || "");
      })
      .catch((err) => console.error("Nie udało się wczytać zadania", err));
  }, [props.taskId]);

  useEffect(() => {
    if (!props.isOpen) return;
    fetchData();
  }, [props.isOpen, fetchData]);

  return (
    <>
      <Modal
        isOpen={props.isOpen}
        onClose={props.onClose}
        size={{ base: "full", md: "2xl" }}
        scrollBehavior={"inside"}
      >
        <ModalOverlay />
        <ModalContent
        >
          <ModalCloseButton />
          <ModalBody>
            <Flex
              direction={"column"}
              gap={"2"}
              p={4}
              borderRadius={"lg"}
              flex={"1"}
            >
              <Text
                fontSize={"2xl"}
                fontWeight={"bold"}
                color={FORUM.tekstJasny}
                lineHeight={"1.25"}
              >
                {data.title}
              </Text>

              <Flex gap={2} wrap={"wrap"} mt={1}>
                <Text
                  fontSize={"xs"}
                  fontWeight={"semibold"}
                  bg={FORUM.plakietka}
                  color={KOLOR_STANU[data.status] || FORUM.tekst}
                  borderRadius={"full"}
                  px={3}
                  py={1}
                >
                  {STATUS_NAME[mojPrzydzial?.status] || "—"}
                </Text>
                <Text
                  fontSize={"xs"}
                  fontWeight={"semibold"}
                  bg={FORUM.plakietka}
                  color={KOLOR_PRIORITYU[data.piority] || FORUM.tekst}
                  borderRadius={"full"}
                  px={3}
                  py={1}
                >
                  Priorytet: {priorityName(data.piority)}
                </Text>
                {data.deadline && (
                  <Text
                    fontSize={"xs"}
                    fontWeight={"semibold"}
                    bg={FORUM.plakietka}
                    color={FORUM.tekst}
                    borderRadius={"full"}
                    px={3}
                    py={1}
                  >
                    Termin: {formatDate(data.deadline)}
                  </Text>
                )}
              </Flex>

              <Box mt={4}>
                <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony} mb={1.5}>
                  Przenieś do
                </Text>
                <Flex gap={2} wrap={"wrap"}>
                  {columnsFor(props.category)
                    .filter((k) => k.key !== mojPrzydzial?.status)
                    .map((k) => (
                      <Box
                        key={k.key}
                        as={"button"}
                        fontSize={"sm"}
                        px={3}
                        py={1.5}
                        borderRadius={"md"}
                        border={"1px solid"}
                        borderColor={FORUM.obramowanie}
                        bg={FORUM.tlo}
                        color={FORUM.tekst}
                        transition={TRANSITION}
                        _hover={{ borderColor: "#6C99FF", color: FORUM.tekstJasny }}
                        onClick={() => changeStatus(k.key)}
                        disabled={changingStatus}
                      >
                        {k.name}
                      </Box>
                    ))}
                </Flex>
              </Box>

              <Flex gap={8} mt={4} wrap={"wrap"}>
                <Box>
                  <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony} mb={0.5}>
                    Postęp całości
                  </Text>
                  <Text fontSize={"sm"} color={data.done_all ? "#4FB477" : FORUM.tekst}>
                    {data.items_done}/{data.items_total}
                    {data.done_all ? " — ukończone" : ""}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony} mb={0.5}>
                    Utworzone przez
                  </Text>
                  <Text fontSize={"sm"} color={FORUM.tekst}>
                    {data.created_by
                      ? props.peoples.find((o) => o.id === data.created_by)?.nickname ||
                        "Nie odnaleziono nicku"
                      : "Brak"}
                  </Text>
                </Box>
                {dataUtworzenia && (
                  <Box>
                    <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony} mb={0.5}>
                      Data utworzenia
                    </Text>
                    <Text fontSize={"sm"} color={FORUM.tekst}>
                      {new Date(dataUtworzenia).toLocaleDateString("pl-PL", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </Text>
                  </Box>
                )}
              </Flex>

              <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony} mt={5} mb={1}>
                Opis
              </Text>
              <Box
                bg={FORUM.tlo}
                border={"1px solid"}
                borderColor={FORUM.obramowanie}
                borderRadius={"md"}
                px={4}
                py={3}
              >
                <Box fontSize={"sm"} color={FORUM.tekst}>
                  <TaskDescription description={data.description} />
                </Box>
              </Box>

              <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony} mt={5} mb={2}>
                Podział pracy
              </Text>
              {(data.assignments || []).map((assignment) => (
                <AssignmentSection
                  key={assignment.id}
                  assignment={assignment}
                  people={props.peoples}
                  moje={
                    String(assignment.assigned_to) === String(localStorage.getItem("id")) ||
                    (assignment.kind === "team" &&
                      assignment.team === categoryForRole(localStorage.getItem("team")))
                  }
                  canManage={canManage(data)}
                  onChange={fetchData}
                />
              ))}

              {canManage(data) && (
                <AddAssignment
                  taskId={props.taskId}
                  people={props.peoples}
                  boardCategory={props.category}
                  onAdded={fetchData}
                />
              )}

              {(data.log || []).length > 0 && (
                <Box mt={5}>
                  <Flex
                    as={"button"}
                    align={"center"}
                    gap={2}
                    onClick={() => setPokazHistorie((h) => !h)}
                    color={FORUM.tekstPrzygaszony}
                    transition={TRANSITION}
                    _hover={{ color: FORUM.tekstJasny }}
                  >
                    <Text fontSize={"xs"}>
                      {showHistory ? "Ukryj historię" : `Historia (${data.log.length})`}
                    </Text>
                  </Flex>

                  {showHistory && (
                    <Flex direction={"column"} gap={1} mt={2}>
                      {data.log.map((entry) => (
                        <Flex key={entry.id} gap={2} fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
                          <Text minW={"78px"} whiteSpace={"nowrap"}>
                            {new Date(entry.created_at).toLocaleString("pl-PL", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Text>
                          <Text color={FORUM.tekst} noOfLines={1}>
                            <Text as={"span"} color={"#6C99FF"}>
                              {entry.actor_name}
                            </Text>{" "}
                            {entry.event}
                            {entry.details ? ` — ${entry.details}` : ""}
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Box>
              )}
              <Flex
                justify={"flex-end"}
                gap={2}
                mt={6}
                wrap={"wrap"}
                direction={{ base: "column-reverse", sm: "row" }}
              >
                <Button
                  variant={copied ? "glowny" : "drugorzedny"}
                  size={"sm"}
                  leftIcon={<FaLink size={12} />}
                  onClick={copyLink}
                  w={{ base: "100%", sm: "auto" }}
                >
                  {copied ? "Skopiowano link" : "Kopiuj link do zadania"}
                </Button>
                {canManage(data) && (
                  <Button
                    variant={"glowny"}
                    size={"sm"}
                    onClick={onEditOpen}
                    w={{ base: "100%", sm: "auto" }}
                  >
                    Edytuj zadanie
                  </Button>
                )}
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={isEditOpen} onClose={onEditClose} size={{ base: "full", md: "md" }} scrollBehavior={"inside"}>
        <ModalOverlay />
        <ModalContent
        >
          <ModalHeader>Edytuj zadanie</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex
              direction={"column"}
              gap={"2"}
              p={4}
              borderRadius={"lg"}
              flex={"1"}
            >
              <Text fontSize={"xl"} align={"left"} fontWeight={"bold"}>
                Tytuł
              </Text>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              <Text fontSize={"xl"} align={"left"} fontWeight={"bold"}>
                Opis
              </Text>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                h={"220px"}
                minH={"220px"}
                resize={"vertical"}
                overflowY={"auto"}
                whiteSpace={"pre-wrap"}
              />
              <Text fontSize={"xl"} align={"left"} fontWeight={"bold"}>
                Priorytet
              </Text>
              <Select
                placeholder="Wybierz priorytet"
                value={piority}
                onChange={(e) => setPiority(e.target.value)}
              >
                <option value="niski">Niski</option>
                <option value="sredni">Średni</option>
                <option value="wysoki">Wysoki</option>
              </Select>
              <Text fontSize={"xl"} align={"left"} fontWeight={"bold"}>
                Termin
              </Text>
              <Input
                type={"date"}
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
              />
              <Text fontSize={"xl"} align={"left"} fontWeight={"bold"}>
                Zdjęcia
              </Text>
              <ImagePicker images={noweZdjecia} onChange={setNoweZdjecia} />
              <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
                Nowe zdjęcia dopiszą się na końcu opisu. Te dodane wcześniej zostają
                w opisie powyżej — usuwa się je, kasując ich adres.
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter gap={"2"}>
            <Button
              colorScheme="red"
              onClick={() => {
                axios
                  .post(
                    `${API_URL}/tasks/delete/${props.taskId}`,
                    {
                      category: props.category,
                    },
                    {
                      withCredentials: true,
                      headers: {
                        Authorization: `Bearer ${getToken()}`,
                      },
                    }
                  )
                  .then((res) => {
                    console.log(res);

                    onEditClose();
                    props.onClose();
                  })
                  .catch((err) => {
                    console.log(err);
                  });
              }}
            >
              Usuń Zadanie
            </Button>
            <Button colorScheme="green" onClick={handleTaskUpdate}>
              Zapisz
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TaskModal;
